import { State, process } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import React, { FC, useCallback, useMemo, useState } from 'react';
import uuid from 'react-uuid';
import useReceiptLineItemsColumns from 'src/assets/static/receipt_details/useLineItemColumns';
import GridWrapper from 'src/components/GridWrapper';
import Modal from 'src/components/Modal';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Accordion from 'src/components/design-system/Accordion';
import Button, {
  isButtonDisabled,
  isButtonEnabled,
  isButtonHidden,
} from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';

import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import useBoolean from 'src/hooks/useBoolean';
import useReceiptDetails from 'src/hooks/useReceiptDetails';
import useScrollDown from 'src/hooks/useScrollDown';
import { ReceiptLineItem } from 'src/services/useReceiptLineItemsApi';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { AddLineItem } from '../../components/guards/PermissionsList';
import CombineLineItems from './CombineLineItemsWizard';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

export const initialLineItemsDataState: State = {
  sort: [
    {
      field: 'poLineItemNumber',
      dir: 'asc',
    },
  ],
};

const defaultProps = {
  className: 'receiptLineItem',
  showColumnMenu: true,
  tableTextId: 'Line items',
  pageable: false,
  DataItemKey: 'rowId',
  resizable: true,
  sortable: true,
  selectable,
};

interface ReceiptDetailsLineItemsProps {
  toggleModal: any;
}

const ReceiptDetailsLineItems: FC<ReceiptDetailsLineItemsProps> = ({
  toggleModal,
}: ReceiptDetailsLineItemsProps) => {
  const {
    receiptLineItems,
    setReceiptLineItems,
    isReceiptLineItemsLoading,
    recId,
    glCodesObject,
    fullyCompletedItemNotes,
    isInProgress,
    lineItemsWithErrorWarningStatus,
    isAddLineItemButtonDisabled,
    selectedLineItemIds,
    setSelectedLineItemIds,
    showScans,
  } = useReceiptDetails();

  const [lineItemsDataState, setLineItemsDataState] = useState<State>(initialLineItemsDataState);
  const [isClick, setIsClick] = useState(false);
  const { data, total } = useMemo(
    () => process(lineItemsWithErrorWarningStatus, lineItemsDataState),
    [lineItemsDataState, lineItemsWithErrorWarningStatus]
  );
  const tableRef = useScrollDown(isClick, setIsClick, [data]);
  const columns = useReceiptLineItemsColumns(tableRef);

  const {
    value: showCombineLineItems,
    setFalse: closeCombineLineItems,
    setTrue: openCombineLineItems,
  } = useBoolean();
  const receiptNotInProgress = !isInProgress;
  const selectedRows = useMemo(
    () =>
      receiptLineItems.filter((val) =>
        selectedLineItemIds.includes(val.receivingReceiptLineItemId)
      ),
    [receiptLineItems, selectedLineItemIds]
  );
  const selectedRowsCount = selectedRows?.length;
  const onLineItemsDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setLineItemsDataState(e.dataState);
  }, []);

  const {
    isCombinedLineItemDisabled,
    combineLineItemMessage,
    isPOQtyOfItemWithLeastItemNumZero,
    leastItemNumber,
    itemWithLeastItemNumber,
    currentSelectedCount,
  } = useMemo(() => {
    let combineLineItemMessage: string | null = '';
    const notAtleastOneZeroPOQty =
      selectedRows?.filter((lineItemObj: any) => lineItemObj.poQty === 0).length === 0;
    const currentSelectedCount = selectedRows.length;

    const leastItemNumber = Math.min(
      selectedRows?.[0]?.poLineItemNumber,
      selectedRows?.[1]?.poLineItemNumber
    );

    const bigItemNumber = Math.max(
      selectedRows?.[0]?.poLineItemNumber,
      selectedRows?.[1]?.poLineItemNumber
    );

    const itemWithLeastItemNumber: ReceiptLineItem | undefined = selectedRows.find(
      (item) => item.poLineItemNumber === leastItemNumber
    );

    const lineItemWithBigLineItemNumber: ReceiptLineItem | undefined = selectedRows.find(
      (item) => item.poLineItemNumber === bigItemNumber
    );

    const isLineItemWithBigItemNumberNonDeletable =
      lineItemWithBigLineItemNumber &&
      (isButtonDisabled(lineItemWithBigLineItemNumber.canDeleteReceiptLineItem) ||
        isButtonHidden(lineItemWithBigLineItemNumber.canDeleteReceiptLineItem));

    const isPOQtyOfItemWithLeastItemNumZero = itemWithLeastItemNumber?.poQty === 0;
    if (!isInProgress) {
      combineLineItemMessage = 'The receipt is not currently in progress.';
    } else if (currentSelectedCount !== 2) {
      combineLineItemMessage = 'Please select exactly 2 Items to Combine.';
    } else if (notAtleastOneZeroPOQty) {
      combineLineItemMessage = 'At least one of the selected Items should have PO Quantity of 0.';
    } else if (
      selectedRows[0]?.isNewlyAddedLineItemNow ||
      selectedRows[1]?.isNewlyAddedLineItemNow
    ) {
      combineLineItemMessage = 'Please save the newly added Line Item on the receipt';
    } else if (receiptNotInProgress) {
      combineLineItemMessage = 'The receipt is not currently in progress.';
    } else if (isLineItemWithBigItemNumberNonDeletable) {
      combineLineItemMessage =
        'The second item selected has been used on a completed receipt or document; it is not eligible to be combined with the first item chosen and deleted.';
    } else {
      combineLineItemMessage = '';
    }

    const isCombinedLineItemDisabled =
      receiptNotInProgress ||
      currentSelectedCount !== 2 ||
      notAtleastOneZeroPOQty ||
      !!isLineItemWithBigItemNumberNonDeletable ||
      !!selectedRows[0]?.isNewlyAddedLineItemNow ||
      !!selectedRows[1]?.isNewlyAddedLineItemNow;

    return {
      isCombinedLineItemDisabled,
      combineLineItemMessage,
      isPOQtyOfItemWithLeastItemNumZero,
      leastItemNumber,
      itemWithLeastItemNumber,
      currentSelectedCount,
    };
  }, [selectedRows, isInProgress, receiptNotInProgress]);

  const onSelectionItemChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      if (event?.dataItem == null) {
        return;
      }
      setSelectedLineItemIds((prev) =>
        prev.includes(event.dataItem.receivingReceiptLineItemId)
          ? prev.filter((item) => item !== event.dataItem.receivingReceiptLineItemId)
          : [...prev, event.dataItem.receivingReceiptLineItemId]
      );
    },
    [setSelectedLineItemIds]
  );

  const handleAddLineItem = useCallback(() => {
    setReceiptLineItems((lineItems) => [
      ...lineItems,
      {
        rowId: uuid(),
        canDeleteReceiptLineItem: 0,
        currentDocQty: 0,
        item: '',
        itemDescription: '',
        itemNoteId: fullyCompletedItemNotes?.itemNoteId,
        itemNoteValue: 'Fully Accepted',
        poQty: 0,
        poglCode: '',
        previousDocQty: 0,
        uom: '',
        isNewLineItem: true,
        complete: 'N',
        poLineItemNumber: lineItems.length + 1,
        varianceQty: 0,
        receivingReceiptLineItemId: Number(`${parseInt(String(Math.random() * 100000))}987654321`),
        poglCodeDescription: '',
        isNewlyAddedLineItemNow: true,
      } as ReceiptLineItem,
    ]);
    setIsClick(true);
  }, [fullyCompletedItemNotes?.itemNoteId, setIsClick, setReceiptLineItems]);

  const handleSetZeroQuantity = useCallback(() => {
    setReceiptLineItems((prev) =>
      [...prev].map((item) => ({
        ...item,
        ...(selectedLineItemIds.includes(item.receivingReceiptLineItemId) && { currentDocQty: 0 }),
      }))
    );
    setSelectedLineItemIds([]);
  }, [selectedLineItemIds, setReceiptLineItems, setSelectedLineItemIds]);

  const updateGLItems = useCallback(
    (glCode: string) => {
      const selectedLineItemIds = selectedRows.map((item) => item.rowId);
      setReceiptLineItems((prev) =>
        prev.map((lineItem) => {
          if (selectedLineItemIds.includes(lineItem.rowId)) {
            return {
              ...lineItem,
              poglCode: glCode,
              poglCodeDescription: glCodesObject[glCode].label,
            };
          }
          return lineItem;
        })
      );
      setSelectedLineItemIds([]);
    },
    [glCodesObject, selectedRows, setReceiptLineItems, setSelectedLineItemIds]
  );

  const handleAssignGLCodes = useCallback(() => {
    toggleModal('Bulk Assign GL Codes', { updateGLItems });
  }, [toggleModal, updateGLItems]);

  const onItemChange = useCallback(
    (event: GridItemChangeEvent) => {
      let { dataItem, field = '', value = '' } = event;
      const { rowId = '' } = dataItem;

      setReceiptLineItems((prev) => {
        let prevLineItems = [...prev];
        if (field === 'itemNoteValue') {
          const { id, text } = value;
          dataItem = {
            ...dataItem,
            itemNoteCode: id,
            [field]: text,
          };
          prevLineItems = prevLineItems?.map((data: any) => {
            if (data?.rowId === rowId) {
              return { ...data, itemNoteCode: id, [field]: text };
            }
            return data;
          });
        } else {
          dataItem = {
            ...dataItem,
            [field]: value,
          };
          prevLineItems = prevLineItems?.map((data: any) => {
            if (data?.rowId === rowId) {
              return { ...data, [field]: value };
            }
            return data;
          });
        }
        return prevLineItems;
      });
    },
    [setReceiptLineItems]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const { checked } = event.syntheticEvent.target as any;
      setSelectedLineItemIds(
        checked ? receiptLineItems.map((i) => i.receivingReceiptLineItemId) : []
      );
    },
    [receiptLineItems, setSelectedLineItemIds]
  );

  const clearFilters = useCallback(() => {
    setLineItemsDataState({ ...initialLineItemsDataState });
  }, []);

  const selectedLineItemEditableCount = useMemo(
    () =>
      selectedRows.filter(
        (val: any) => val?.poQty === 0 && isButtonEnabled(val?.canDeleteReceiptLineItem)
      )?.length,
    [selectedRows]
  );

  const disableBulkAssign = useMemo(
    () =>
      !(
        selectedRowsCount > 1 &&
        isInProgress &&
        selectedRowsCount === selectedLineItemEditableCount
      ),
    [isInProgress, selectedLineItemEditableCount, selectedRowsCount]
  );

  const disableSetZeroQty = useMemo(
    () =>
      !currentSelectedCount ||
      !isInProgress ||
      !receiptLineItems?.some(
        (row) =>
          selectedLineItemIds.includes(row.receivingReceiptLineItemId) &&
          Number(row.currentDocQty) !== 0
      ),
    [currentSelectedCount, isInProgress, receiptLineItems, selectedLineItemIds]
  );

  const isClearFiltersDisabled = useMemo(
    () => !lineItemsDataState.filter?.filters.length,
    [lineItemsDataState.filter?.filters.length]
  );

  const gridToolbarChildrenProps: GridToolbarChildrenProps = useMemo(
    () => ({
      setZeroQuantity: handleSetZeroQuantity,
      assignGLCodes: handleAssignGLCodes,
      openCombineLineItems,
      addLineItem: handleAddLineItem,
      disableAssignGLBtn: disableBulkAssign,
      disableSetZeroQty,
      disableCombineLineItems: isCombinedLineItemDisabled,
      isAddLineItemButtonDisabled: isAddLineItemButtonDisabled,
      showCombineLineItems: showCombineLineItems,
      setReceiptLineItems: setReceiptLineItems,
      receiptLineItems: receiptLineItems,
      selectedRows,
      combineLineItemMessage: combineLineItemMessage,
      clearFilters,
      closeCombineLineItems,
      isPOQtyOfItemWithLeastItemNumZero,
      leastItemNumber,
      itemWithLeastItemNumber,
      isInProgress,
      selectedLineItemEditableCount,
      isClearFiltersDisabled,
    }),
    [
      handleSetZeroQuantity,
      handleAssignGLCodes,
      openCombineLineItems,
      handleAddLineItem,
      disableBulkAssign,
      disableSetZeroQty,
      isCombinedLineItemDisabled,
      isAddLineItemButtonDisabled,
      showCombineLineItems,
      setReceiptLineItems,
      receiptLineItems,
      selectedRows,
      combineLineItemMessage,
      clearFilters,
      closeCombineLineItems,
      isPOQtyOfItemWithLeastItemNumZero,
      leastItemNumber,
      itemWithLeastItemNumber,
      isInProgress,
      selectedLineItemEditableCount,
      isClearFiltersDisabled,
    ]
  );

  return (
    <>
      <Accordion title={`Line Items for Receipt #${recId}`} defaultExpanded>
        <div className="mt-1 mb-1 px-1">
          <GridWrapper
            loading={isReceiptLineItemsLoading}
            columns={columns}
            dataSource={data || []}
            dataState={lineItemsDataState}
            onItemChange={onItemChange}
            total={total}
            ref={tableRef}
            onDataStateChange={onLineItemsDataStateChange}
            onSelectionChange={onSelectionItemChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            gridToolbarChildren={<GridToolbarChildren {...gridToolbarChildrenProps} />}
            {...defaultProps}
            className={'max-h-[400px]'}
            key={`${showScans}`}
          />
        </div>
      </Accordion>
    </>
  );
};

interface GridToolbarChildrenProps {
  setZeroQuantity: VoidFunction;
  assignGLCodes: VoidFunction;
  openCombineLineItems: VoidFunction;
  addLineItem: VoidFunction;
  disableAssignGLBtn: boolean;
  disableSetZeroQty: boolean;
  disableCombineLineItems: boolean;
  isAddLineItemButtonDisabled: boolean;
  showCombineLineItems: boolean;
  setReceiptLineItems: React.Dispatch<any>;
  receiptLineItems: any;
  selectedRows: any[];
  combineLineItemMessage: string | null;
  clearFilters: VoidFunction;
  closeCombineLineItems: VoidFunction;
  leastItemNumber: number;
  isPOQtyOfItemWithLeastItemNumZero: boolean;
  itemWithLeastItemNumber?: ReceiptLineItem;
  isInProgress: boolean;
  selectedLineItemEditableCount: number;
  isClearFiltersDisabled: boolean;
}

const GridToolbarChildren: FC<GridToolbarChildrenProps> = ({
  setZeroQuantity,
  assignGLCodes,
  openCombineLineItems,
  addLineItem,
  disableAssignGLBtn,
  disableSetZeroQty,
  disableCombineLineItems,
  isAddLineItemButtonDisabled,
  showCombineLineItems,
  setReceiptLineItems,
  receiptLineItems,
  selectedRows,
  combineLineItemMessage,
  clearFilters,
  closeCombineLineItems,
  isPOQtyOfItemWithLeastItemNumZero,
  itemWithLeastItemNumber,
  isInProgress,
  selectedLineItemEditableCount,
  isClearFiltersDisabled,
}) => {
  const isCurrentDocQtyZero = useMemo(
    () => selectedRows.some((row: any) => row?.currentDocQty === 0),
    [selectedRows]
  );

  const addLineItemContent = useMemo(() => {
    let content = '';
    if (!isInProgress) {
      content = 'The receipt is not currently in progress.';
    } else {
      content = '';
    }
    return content;
  }, [isInProgress]);

  const setZeroQtyContent = useMemo(() => {
    let content = '';
    const isTwoLineItemsSelected = selectedRows.length >= 1;
    if (!isInProgress) {
      content = 'The receipt is not currently in progress.';
    } else if (!isTwoLineItemsSelected) {
      content = 'Please select at least 1 Item to Set Quantity to Zero.';
    } else if (isCurrentDocQtyZero) {
      content = 'The selected line item(s) should not have 0 Quantity';
    } else {
      content = '';
    }
    return content;
  }, [isCurrentDocQtyZero, isInProgress, selectedRows.length]);

  const bulkAssignGLContent = useMemo(() => {
    let content = '';
    if (!isInProgress) {
      content = 'The receipt is not currently in progress.';
    } else if (selectedRows.length > 0 && selectedLineItemEditableCount !== selectedRows.length) {
      content = 'The selected line item(s) originate from the  purchase order.';
    } else if (selectedRows.length <= 1) {
      content = 'Please select at least 2 Items to Bulk Assign GL Codes.';
    } else {
      content = '';
    }
    return content;
  }, [isInProgress, selectedLineItemEditableCount, selectedRows]);

  return (
    <>
      <ClearFilterButton disabled={isClearFiltersDisabled} onClick={clearFilters} />
      {[
        ...(!isInProgress
          ? []
          : [
              {
                clickFunction: setZeroQuantity,
                color: 'primary',
                child: 'Set Zero Quantity',
                disabled: disableSetZeroQty,
                content: setZeroQtyContent,
                permissions: [],
              },
              {
                clickFunction: assignGLCodes,
                color: 'primary',
                child: 'Bulk Assign GL Codes',
                disabled: disableAssignGLBtn,
                content: bulkAssignGLContent,
                permissions: [],
              },
              {
                clickFunction: openCombineLineItems,
                color: 'primary',
                child: 'Combine Line Items',
                disabled: disableCombineLineItems,
                content: combineLineItemMessage,
                permissions: [],
              },
              {
                clickFunction: addLineItem,
                color: 'primary',
                child: 'Add Line Item',
                disabled: isAddLineItemButtonDisabled,
                content: addLineItemContent,
                permissions: AddLineItem,
              },
            ]),
      ].map((btnObj: any) => (
        <PopUp
          show={btnObj.disabled}
          key={btnObj.child}
          content={
            <div className="w-[350px]">
              <PopUpContentWrapper type="Information" contents={[btnObj.content]} />
            </div>
          }
        >
          <PermissionGuard permissions={btnObj.permissions}>
            <Button
              key={btnObj.child}
              color={btnObj.color}
              disabled={btnObj.disabled}
              onClick={btnObj.clickFunction}
            >
              {btnObj.child}
            </Button>
          </PermissionGuard>
        </PopUp>
      ))}
      <Modal
        width="50vw"
        title="Combine Receipt Items"
        visible={showCombineLineItems}
        closeModal={closeCombineLineItems}
      >
        <CombineLineItems
          combinableLineItems={selectedRows.map((i) => ({ ...i, selected: false }))}
          close={closeCombineLineItems}
          setReceiptLineItems={setReceiptLineItems}
          receiptLineItems={receiptLineItems}
          isPOQtyOfItemWithLeastItemNumZero={isPOQtyOfItemWithLeastItemNumZero}
          itemWithLeastItemNumber={itemWithLeastItemNumber as ReceiptLineItem}
        />
      </Modal>
    </>
  );
};

export default React.memo(ReceiptDetailsLineItems);
